import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Member } from "src/redux/types/_customer.type";
import BasicInfo from "./components/basic-info";
import ViewHealthReport from "./components/view-health-report";
import ViewDietaryReport from "./components/view-dietary-report";
import { ArrowLeft } from "lucide-react";
import { useFetchMembers } from "./hooks/useFetchMembers";

const ViewReport = () => {
  const { name, subId } = useParams();
  const navigate = useNavigate();

  const { data: members, isLoading } = useFetchMembers(Number(subId));

  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const onChange = (id: string) => {
    const member = members.find((m: Member) => m.id === Number(id));

    if (member) {
      setSelectedMember(member);
    }
  };

  const updateSelected = (member: Member) => {
    setSelectedMember(member);
  };

  if (isLoading) {
    return (
      <div className="text-center w-[80%] mx-auto">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="px-4 relative">
      <header className="py-3 pl-4 pr-8 w-full rounded-lg h-14 bg-white/30 backdrop-blur-md shadow-md sticky top-0 flex items-center z-50">
        <button
          className="p-2 rounded-full text-sm  hover:bg-gray-200 border flex items-center justify-center duration-300 ease-in-out transition-all"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="size-4" />
        </button>
        <h1 className="text-center text-lg font-semibold  w-full">
          {`View Health Report for Member/s of ${name}`}
        </h1>
      </header>

      {!selectedMember?.id && (
        <div className="p-3 rounded-xl bg-amber-50 max-w-xl mx-auto my-3">
          <p className="text-md font-semibold text-amber-600 text-center">
            Select a Member to View Reports
          </p>
        </div>
      )}

      <div className="max-w-lg pt-3 mx-auto">
        <Select onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select Member to View Reports" />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(members) &&
              members.map((member: Member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {selectedMember?.id && (
        <div className="sticky top-0 justify-center bg-white py-2 rounded-xl max-w-3xl mx-auto my-3 z-50">
          <h3 className="capitalize text-lg font-semibold text-primary text-center">
            Health Report of {selectedMember.first_name}{" "}
            {selectedMember.last_name}
          </h3>
        </div>
      )}

      {selectedMember && (
        <>
          <BasicInfo member={selectedMember} updateSelected={updateSelected} />
          <ViewHealthReport memberId={Number(selectedMember?.id)} />

          <div className="mb-12">
            <ViewDietaryReport memberId={Number(selectedMember?.id)} />
          </div>
        </>
      )}
    </div>
  );
};

export default ViewReport;
